import { patchState, signalStoreFeature } from '@ngrx/signals';
import { Events, withEventHandlers } from '@ngrx/signals/events';
import { mapResponse } from '@ngrx/operators';
import { concatMap, delay, filter, from, of, switchMap, tap } from 'rxjs';

import { TasksService } from '../../../services';
import { TASK_DETAIL_EVENTS, TASK_EVENTS } from '../../signal-events';
import {
  NON_CANCELLED_TASK_STATUSES,
  TaskInstanceDetail,
} from '@data-access/tasks/types';
import { extractBlobAndFilename, saveAs } from '@shared/utils/download.util';

/**
 * Helper function to search for a task and either retrieve its details or create a new one
 * @private
 * @returns Observable of task detail or creation event
 */
const _searchOrCreateTask = <TPayload>(options: {
  createEvent: (params: { payload: TPayload; workspaceID: string }) => unknown;
  payload: TPayload;
  searchParams: Record<string, unknown>;
}) => {
  const { searchParams, payload, createEvent } = options;
  return (taskListService: TasksService, workspaceID: string) =>
    taskListService.getTaskList(workspaceID, searchParams).pipe(
      filter(Boolean),
      concatMap((response) => {
        // if no task exists, create one
        if (response.length === 0) {
          return of(createEvent({ payload, workspaceID }));
        }
        // if a task already exists, fetch its details
        // and if a thread exists, fetch its messages
        const retrieveTaskInfo = [
          TASK_DETAIL_EVENTS.getTaskDetail({
            taskInstanceId: response[0].id,
            workspaceID,
          }),
          // Either fetch messages if a thread exists or patch with an empty array
          ...(response[0].thread?.id
            ? [
                TASK_DETAIL_EVENTS.getTaskMessages({
                  threadId: response[0].thread.id,
                  workspaceID,
                }),
              ]
            : [TASK_DETAIL_EVENTS.patchMessages({ messages: [] })]),
        ];
        return from(retrieveTaskInfo);
      }),
    );
};

export const withTaskDetailEffects = (
  _events: Events,
  _taskListService: TasksService,
) =>
  signalStoreFeature(
    withEventHandlers(({ ...store }) => ({
      addEntityToTask$: _events.on(TASK_DETAIL_EVENTS.addEntityToTask).pipe(
        mapResponse({
          error: () => {},
          next: (response) => {
            return TASK_DETAIL_EVENTS.patchTaskDetail({
              error: null,
              lastRefreshedTaskId: '',
              messageCommunicationEstablished: true,
              taskDetail: response.payload
                .data as unknown as TaskInstanceDetail,
            });
          },
        }),
      ),

      clearTaskDetails$: _events.on(TASK_DETAIL_EVENTS.clearTaskDetail).pipe(
        mapResponse({
          error: () => {},
          next: () => {
            patchState(store, { surveyInstance: null });
            return TASK_DETAIL_EVENTS.patchTaskDetail({
              error: null,
              lastRefreshedTaskId: '',
              messageCommunicationEstablished: false,
              taskDetail: null,
            });
          },
        }),
      ),

      createDocumentTask$: _events
        .on(TASK_DETAIL_EVENTS.createDocumentTask)
        .pipe(
          tap(() => patchState(store, { messages: null, taskDetail: null })),
          switchMap((event) => {
            const { workspaceID, payload } = event.payload;
            return _taskListService
              .createDocumentTask(workspaceID, payload)
              .pipe(
                filter(Boolean),
                mapResponse({
                  error: (error: Error) => patchState(store, { error }),
                  next: (response) =>
                    // populate the task details after creation
                    TASK_DETAIL_EVENTS.getTaskDetail({
                      taskInstanceId: response.id,
                      workspaceID,
                    }),
                }),
              );
          }),
        ),

      createQuestionTask$: _events
        .on(TASK_DETAIL_EVENTS.createQuestionTask)
        .pipe(
          tap(() => patchState(store, { messages: null, taskDetail: null })),
          switchMap((event) => {
            const { workspaceID, payload } = event.payload;
            return _taskListService
              .createQuestionTask(workspaceID, payload)
              .pipe(
                filter(Boolean),
                mapResponse({
                  error: (error: Error) => patchState(store, { error }),
                  next: (response) =>
                    // populate the task details after creation
                    TASK_DETAIL_EVENTS.getTaskDetail({
                      taskInstanceId: response.id,
                      workspaceID,
                    }),
                }),
              );
          }),
        ),
      createSectionTask$: _events.on(TASK_DETAIL_EVENTS.createSectionTask).pipe(
        tap(() => patchState(store, { messages: null, taskDetail: null })),
        switchMap((event) => {
          const { workspaceID, payload } = event.payload;
          return _taskListService.createSectionTask(workspaceID, payload).pipe(
            filter(Boolean),
            mapResponse({
              error: (error: Error) => patchState(store, { error }),
              next: (response) =>
                // populate the task details after creation
                TASK_DETAIL_EVENTS.getTaskDetail({
                  taskInstanceId: response.id,
                  workspaceID,
                }),
            }),
          );
        }),
      ),

      // Create a new message in the task thread
      createTaskMessage$: _events.on(TASK_DETAIL_EVENTS.createTaskMessage).pipe(
        tap(() => patchState(store, { messages: null, taskDetail: null })),
        switchMap((event) => {
          const { workspaceID, taskInstanceId, message } = event.payload;
          return _taskListService
            .createThreadMessage(workspaceID, taskInstanceId, message)
            .pipe(
              filter(Boolean),
              concatMap((response) => {
                return from([
                  // refresh the remaining to-do task count
                  TASK_EVENTS.getRemainingToDoTaskCount({ workspaceID }),
                  // refresh the task list
                  TASK_EVENTS.getTaskList({ submissionStage: '', workspaceID }),
                  // fetch updated messages for the thread
                  TASK_DETAIL_EVENTS.getTaskMessages({
                    threadId: response?.thread_id ?? '',
                    workspaceID,
                  }),
                  // fetch updated task details
                  TASK_DETAIL_EVENTS.getTaskDetail({
                    taskInstanceId,
                    workspaceID,
                  }),
                ]);
              }),
            );
        }),
      ),

      // Export all messages in a task thread as a file
      exportThreadMessages$: _events
        .on(TASK_DETAIL_EVENTS.exportThreadMessages)
        .pipe(
          switchMap((event) => {
            const { workspaceID, threadId } = event.payload;
            return _taskListService
              .exportThreadMessages(workspaceID, threadId)
              .pipe(
                filter(Boolean),
                mapResponse({
                  error: (error: Error) => patchState(store, { error }),
                  next: (response) => {
                    const extracted = extractBlobAndFilename(response);
                    const { blob, filename } = extracted;
                    // Saves the file using the extracted blob and filename
                    saveAs(blob, filename);
                  },
                }),
              );
          }),
        ),

      getTaskDetails$: _events.on(TASK_DETAIL_EVENTS.getTaskDetail).pipe(
        tap(() =>
          patchState(store, { messageCommunicationEstablished: false }),
        ),
        switchMap((event) => {
          const { workspaceID, taskInstanceId } = event.payload;
          return _taskListService.getTaskById(workspaceID, taskInstanceId).pipe(
            delay(200),
            filter(Boolean),
            mapResponse({
              error: (error: Error) =>
                patchState(store, {
                  error,
                  messageCommunicationEstablished: false,
                }),
              next: (response) =>
                TASK_DETAIL_EVENTS.patchTaskDetail({
                  error: null,
                  lastRefreshedTaskId: '',
                  messageCommunicationEstablished: true,
                  taskDetail: response,
                }),
            }),
          );
        }),
      ),
      // Get all messages in a task thread
      getTaskMessages$: _events.on(TASK_DETAIL_EVENTS.getTaskMessages).pipe(
        switchMap((event) => {
          const { workspaceID, threadId } = event.payload;
          return _taskListService.getThreadMessages(workspaceID, threadId).pipe(
            filter(Boolean),
            mapResponse({
              error: (error: Error) => patchState(store, { error }),
              next: (response) =>
                TASK_DETAIL_EVENTS.patchMessages({ messages: response }),
            }),
          );
        }),
      ),

      patchMessages$: _events.on(TASK_DETAIL_EVENTS.patchMessages).pipe(
        mapResponse({
          error: () => {},
          next: (response) =>
            patchState(store, { messages: [...response.payload.messages] }),
        }),
      ),

      // Refresh task detail when a new message is created via websocket
      refreshTaskDetail$: _events.on(TASK_DETAIL_EVENTS.refreshTaskDetail).pipe(
        tap((event) => {
          // Update the refreshed task ID in the store so components can react
          patchState(store, {
            lastRefreshedTaskId: event.payload.taskDetailId,
          });
        }),
      ),

      // Event on search for a question task. If it exists, fetch its details and messages.
      searchDocumentTask$: _events
        .on(TASK_DETAIL_EVENTS.searchDocumentTask)
        .pipe(
          switchMap((event) => {
            const { workspaceID, payload } = event.payload;
            return _searchOrCreateTask({
              createEvent: TASK_DETAIL_EVENTS.createDocumentTask,
              payload,
              searchParams: {
                document_id: payload.document_id,
                status: [...NON_CANCELLED_TASK_STATUSES],
                task_types: ['document'],
              },
            })(_taskListService, workspaceID);
          }),
        ),
      // Event on search for a question task. If it exists, fetch its details and messages.
      searchQuestionTask$: _events
        .on(TASK_DETAIL_EVENTS.searchQuestionTask)
        .pipe(
          switchMap((event) => {
            const { workspaceID, payload } = event.payload;
            return _searchOrCreateTask({
              createEvent: TASK_DETAIL_EVENTS.createQuestionTask,
              payload,
              searchParams: {
                question_id: payload.question_id,
                status: [...NON_CANCELLED_TASK_STATUSES],
                task_types: ['question'],
              },
            })(_taskListService, workspaceID);
          }),
        ),

      searchSectionTask$: _events.on(TASK_DETAIL_EVENTS.searchSectionTask).pipe(
        switchMap((event) => {
          const { workspaceID, payload } = event.payload;
          return _searchOrCreateTask({
            createEvent: TASK_DETAIL_EVENTS.createSectionTask,
            payload,
            searchParams: {
              section_id: payload.section_id,
              status: [...NON_CANCELLED_TASK_STATUSES],
              task_types: ['section'],
            },
          })(_taskListService, workspaceID);
        }),
      ),
      updateTaskStatus$: _events.on(TASK_DETAIL_EVENTS.updateTaskStatus).pipe(
        switchMap((event) => {
          const {
            status: _status,
            taskInstanceId: _taskInstanceId,
            workspaceID: _workspaceID,
          } = event.payload;
          // TODO: Finish Implementation in followup PR
          return of(true);
        }),
      ),
    })),
  );
